<template>
	<EventCenterView
		ref="eventCenterView"
		:event-center-disabled="eventCenterDisabled"
		:show-scope-tabs="showScopeTabs"
		:navigation-config="navigationConfig"
		:is-competition-view="isCompetitionView"
		:is-loading-events="isLoadingEvents"
		:is-loading-navigation="isLoadingHarness"
		:display-events="displayEvents"
		:query-state="queryState"
		:scope-nav-items="scopeNavItems"
		:all-events-nav-items="allEventsNavItems"
		:competition-options="competitionOptions"
		:competition-content-nav-items="competitionContentNavItems"
		:show-search="showSearch"
		:show-select-filters="showSelectFilters"
		:show-match-days="showMatchDays"
		:event-list-view-mode="eventListViewMode"
		:selected-stage="selectedCompetitionStage"
		:selected-sub-stage-id="queryState.substage"
		:active-filters="activeFilters"
		:pagination="pagination"
		:league-table="leagueTable"
		:top-scorer-tables="topScorerTables"
		:is-table-not-found="isTableNotFound"
		:is-top-scorer-not-found="isTopScorerNotFound"
		:is-loading-table="isLoadingTable"
		:is-loading-top-scorers="isLoadingTopScorerTables"
		@scope-change="handleScopeChange"
		@competition-change="handleCompetitionChange"
		@competition-subview-change="handleCompetitionSubviewChange"
		@competition-content-change="handleCompetitionContentChange"
		@substage-change="handleCompetitionSubStageChange"
		@all-events-view-change="handleAllEventsViewChange"
		@search-change="handleSearchChange"
		@filter-change="handleFilterChange"
		@pagination-change="handlePaginationChange"
	/>
</template>

<script>
import { EventListFilter } from '@shared/enums/EventListFilter';
import { httpClient } from '@shared/js/api/httpClient';
import EventCenterView from './EventCenterView';
import {
	ALL_EVENTS_PER_PAGE,
	CHRONOLOGICAL_SUBVIEW_KEY,
	EVENT_CENTER_VIEWS,
	FILTER_KEYS,
	createDefaultQueryState,
	getVisibilityMode,
	reconcileQueryStateFilters,
	resolveDefaultView,
	sanitizeQueryState,
	selectViewEvents,
	shouldResetPagination,
	toURLParams,
} from './eventCenterState';

const COMPETITION_CONTENT_VIEWS = Object.freeze({
	FIXTURES_RESULTS: 'fixtures-results',
	TABLE:            'table',
	TOP_SCORER:       'top-scorer',
});

const normalizePaginationMeta = (meta = null) => {
	if (!meta || typeof meta !== 'object') {
		return null;
	}

	const currentPage = Number(meta.currentPage);
	const lastPage = Number(meta.lastPage);
	const perPage = Number(meta.perPage);
	const total = Number(meta.total);

	if (![currentPage, lastPage, perPage, total].every(Number.isFinite)) {
		return null;
	}

	return {
		currentPage,
		lastPage,
		perPage,
		total,
	};
};

const shouldFetchEventsFromServer = (previousState, nextState) => {
	if (!previousState) {
		return true;
	}

	if (nextState.view !== EVENT_CENTER_VIEWS.COMPETITIONS) {
		return true;
	}

	if (previousState.view !== EVENT_CENTER_VIEWS.COMPETITIONS) {
		return true;
	}

	return String(previousState.competition || '') !== String(nextState.competition || '');
};

const shouldResetCompetitionContentData = (previousState, nextState) => {
	if (!previousState) {
		return nextState.view === EVENT_CENTER_VIEWS.COMPETITIONS;
	}

	const wasCompetitionView = previousState.view === EVENT_CENTER_VIEWS.COMPETITIONS;
	const isCompetitionView = nextState.view === EVENT_CENTER_VIEWS.COMPETITIONS;

	if (!isCompetitionView) {
		return wasCompetitionView;
	}

	if (!wasCompetitionView) {
		return true;
	}

	return String(previousState.competition || '') !== String(nextState.competition || '');
};

const defaultContextConfig = {
	eventCenterEnabled: true,
	showAllEvents:      true,
	isLeagueContext:    false,
};

const defaultFilterConfig = {
	searchEnabled:     true,
	teamCategoryLabel: null,
	eventTopicLabel:   null,
	enabled:           {
		[EventListFilter.TEAM]:          true,
		[EventListFilter.CLUB]:          true,
		[EventListFilter.LEAGUE]:        true,
		[EventListFilter.SPORT]:         true,
		[EventListFilter.TEAM_CATEGORY]: true,
		[EventListFilter.EVENT_TOPIC]:   true,
	},
};

const resetCompetitionContentView = (component, contentView) => {
	if (component.competitionContentView === contentView) {
		component.competitionContentView = COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS;
	}
};

const getVisibleCompetitionNodes = (competitions = []) => competitions
	.filter(competition => competition?.hasEvents !== false)
	.map(competition => ({
		...competition,
		stages: (competition?.stages || [])
			.filter(stage => stage?.hasEvents !== false)
			.map(stage => ({
				...stage,
				subStages: (stage?.subStages || []).filter(subStage => subStage?.hasEvents !== false),
			})),
	}));

export default {
	name:       'EventCenter',
	components: {
		EventCenterView,
	},
	props: {
		contextId: {
			type:     Number,
			required: true,
		},
		contextType: {
			type:     String,
			required: true,
		},
		initialQuery: {
			type:    Object,
			default: null,
		},
		nowTimestamp: {
			type:    Number,
			default: () => Math.floor(Date.now() / 1000),
		},
	},
	data() {
		return {
			queryState:                  createDefaultQueryState(),
			harnessData:                 null,
			rawEvents:                   [],
			activeFeedFilters:           [],
			eventsPagination:            null,
			isLoadingHarness:            false,
			isLoadingEvents:             false,
			competitionContentView:      COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS,
			leagueTable:                 null,
			topScorerTables:             null,
			isTableNotFound:             false,
			isTopScorerNotFound:         false,
			isLoadingTable:              false,
			isLoadingTopScorerTables:    false,
			tableRequestId:              0,
			topScorerRequestId:          0,
			eventsRequestId:             0,
			hasReportedViewsConfigError: false,
		};
	},
	computed: {
		contextConfig() {
			return this.harnessData?.contextConfig || defaultContextConfig;
		},
		filterConfig() {
			return {
				...defaultFilterConfig,
				...(this.harnessData?.filterConfig || {}),
				enabled: {
					...defaultFilterConfig.enabled,
					...(this.harnessData?.filterConfig?.enabled || {}),
				},
			};
		},
		competitions() {
			return getVisibleCompetitionNodes(this.harnessData?.competitions || []);
		},
		eventCenterDisabled() {
			return this.contextConfig.eventCenterEnabled === false;
		},
		visibility() {
			return getVisibilityMode(this.contextConfig, this.competitions);
		},
		showScopeTabs() {
			return this.visibility.showAllEventsTab && this.visibility.showCompetitionsTab;
		},
		isCompetitionView() {
			return this.queryState.view === EVENT_CENTER_VIEWS.COMPETITIONS;
		},
		showSearch() {
			return !this.isLoadingHarness && this.filterConfig.searchEnabled;
		},
		showSelectFilters() {
			return !this.isLoadingHarness;
		},
		showMatchDays() {
			return !this.isCompetitionView || this.competitionContentView === COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS;
		},
		navigationConfig() {
			return this.harnessData?.navigationConfig || null;
		},
		scopeNavItems() {
			return [
				{ label: this.$I18n.trans('event_center.all_events'), url: 'all-events', active: this.queryState.view !== EVENT_CENTER_VIEWS.COMPETITIONS },
				{ label: this.$I18n.trans_choice('event_center.competition', 2), url: 'competitions', active: this.queryState.view === EVENT_CENTER_VIEWS.COMPETITIONS },
			];
		},
		allEventsNavItems() {
			return [
				{ label: this.$I18n.trans('commons.current'), url: EVENT_CENTER_VIEWS.CURRENT, active: this.queryState.view === EVENT_CENTER_VIEWS.CURRENT },
				{ label: this.$I18n.trans('commons.upcoming'), url: EVENT_CENTER_VIEWS.UPCOMING, active: this.queryState.view === EVENT_CENTER_VIEWS.UPCOMING },
				{ label: this.$I18n.trans_choice('commons.replay', 2), url: EVENT_CENTER_VIEWS.REPLAYS, active: this.queryState.view === EVENT_CENTER_VIEWS.REPLAYS },
			];
		},
		competitionOptions() {
			return this.competitions
				.filter(competition => competition.enabled)
				.map(competition => ({
					label: competition.name,
					value: String(competition.id),
				}));
		},
		selectedCompetition() {
			return this.competitions.find(competition => String(competition.id) === String(this.queryState.competition)) || null;
		},
		selectedCompetitionStage() {
			if (
				!this.isCompetitionView ||
				!this.selectedCompetition ||
				!this.queryState.subview ||
				this.queryState.subview === CHRONOLOGICAL_SUBVIEW_KEY
			) {
				return null;
			}

			return this.selectedCompetition.stages.find(stage => String(stage.id) === String(this.queryState.subview)) || null;
		},
		hasSelectedCompetitionLeagueTable() {
			return Boolean(this.selectedCompetition?.hasLeagueTable);
		},
		hasSelectedCompetitionTopScorerTables() {
			return Boolean(this.selectedCompetition?.hasTopScorerTables);
		},
		eventListViewMode() {
			if (!this.isCompetitionView) {
				return this.queryState.view;
			}

			return this.queryState.subview === CHRONOLOGICAL_SUBVIEW_KEY
				? 'competition-chronological'
				: 'competition-stage';
		},
		competitionContentNavItems() {
			if (!this.isCompetitionView || !this.selectedCompetition) {
				return [];
			}

			const items = [
				{
					label:  this.$I18n.trans('arena.match_center.matchplan_and_results'),
					url:    COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS,
					active: this.competitionContentView === COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS,
				},
			];

			if (!this.isTableNotFound && this.hasSelectedCompetitionLeagueTable) {
				items.push({
					label:  this.$I18n.trans('arena.match_center.table'),
					url:    COMPETITION_CONTENT_VIEWS.TABLE,
					active: this.competitionContentView === COMPETITION_CONTENT_VIEWS.TABLE,
				});
			}

			if (!this.isTopScorerNotFound && this.hasSelectedCompetitionTopScorerTables) {
				items.push({
					label:  this.$I18n.trans('arena.match_center.top_scorer'),
					url:    COMPETITION_CONTENT_VIEWS.TOP_SCORER,
					active: this.competitionContentView === COMPETITION_CONTENT_VIEWS.TOP_SCORER,
				});
			}

			return items;
		},
		activeFilters() {
			return (this.activeFeedFilters || []).map(filter => ({
				...filter,
				value: this.queryState[filter.key] ?? filter.defaultValue ?? null,
			}));
		},
		selectedFeed() {
			if (!this.isCompetitionView) {
				return {
					events:     this.rawEvents,
					pagination: this.eventsPagination,
				};
			}

			return selectViewEvents({
				events:       this.rawEvents,
				queryState:   this.queryState,
				nowTimestamp: this.nowTimestamp,
			});
		},
		displayEvents() {
			return this.selectedFeed.events;
		},
		pagination() {
			return this.selectedFeed.pagination;
		},
	},
	async created() {
		await this.initialize();
	},
	methods: {
		buildRequestParams(queryState = {}) {
			return {
				contextId:   this.contextId,
				contextType: this.contextType,
				...queryState,
			};
		},
		reportAllEventsViewsConfigError() {
			if (this.hasReportedViewsConfigError) {
				return;
			}

			this.hasReportedViewsConfigError = true;
			console.error('EventCenter: missing navigationConfig.allEvents.views in harness response', {
				navigationConfig: this.navigationConfig,
				contextId:        this.contextId,
				contextType:      this.contextType,
			});
			this.$toast.error(this.$I18n.trans('errors.500.title'));
		},
		getEnabledAllEventsViews() {
			const configuredViews = this.navigationConfig?.allEvents?.views;
			if (!Array.isArray(configuredViews) || configuredViews.length === 0) {
				this.reportAllEventsViewsConfigError();
				return [];
			}

			return configuredViews
				.filter(view => view?.enabled !== false)
				.map(view => view?.value)
				.filter(Boolean);
		},
		resolveAllowedAllEventsView(queryState) {
			if (queryState.view === EVENT_CENTER_VIEWS.COMPETITIONS) {
				return queryState;
			}

			const enabledViews = this.getEnabledAllEventsViews();
			if (enabledViews.length === 0) {
				return queryState;
			}

			if (enabledViews.includes(queryState.view)) {
				return queryState;
			}

			return {
				...queryState,
				view: enabledViews[0],
			};
		},
		async initialize() {
			this.isLoadingHarness = true;
			try {
				await this.loadHarness();
				const defaultView = resolveDefaultView({
					contextConfig: this.contextConfig,
					competitions:  this.competitions,
				});

				const fallbackState = {
					...createDefaultQueryState(),
					...defaultView,
				};

				const rawQuery = this.initialQuery || Object.fromEntries(new URLSearchParams(window.location.search).entries());
				const { state } = sanitizeQueryState({
					rawQuery,
					contextConfig: this.contextConfig,
					competitions:  this.competitions,
					fallbackState,
				});

				this.queryState = this.resolveAllowedAllEventsView(state);
				this.syncURL();
				await this.loadEvents();
			} finally {
				this.isLoadingHarness = false;
			}
		},
		async loadHarness() {
			const response = await httpClient.getEventCenterHarness(this.buildRequestParams());
			this.harnessData = response.data;
		},
		async fetchEventsByState(queryState) {
			const requestParams = this.buildRequestParams(queryState);
			if ([EVENT_CENTER_VIEWS.UPCOMING, EVENT_CENTER_VIEWS.REPLAYS].includes(queryState.view)) {
				requestParams.perPage = ALL_EVENTS_PER_PAGE;
			}

			const params = toURLParams(requestParams);
			const response = await httpClient.getEventCenterEvents(params);
			const payload = response.data;

			if (Array.isArray(payload?.data)) {
				return {
					events:     payload.data,
					pagination: normalizePaginationMeta(payload.meta),
					filters:    payload.meta?.filters || [],
				};
			}

			if (Array.isArray(payload)) {
				return {
					events:     payload,
					pagination: null,
					filters:    [],
				};
			}

			return {
				events:     [],
				pagination: null,
				filters:    [],
			};
		},
		async loadEvents() {
			const requestId = ++this.eventsRequestId;
			this.isLoadingEvents = true;
			try {
				const { events, pagination, filters } = await this.fetchEventsByState(this.queryState);
				if (requestId !== this.eventsRequestId) {
					return;
				}
				const reconciledQueryState = reconcileQueryStateFilters(this.queryState, filters);
				if (FILTER_KEYS.some(filterKey => reconciledQueryState[filterKey] !== this.queryState[filterKey])) {
					this.queryState = reconciledQueryState;
					this.syncURL();
				}
				this.rawEvents = events;
				this.activeFeedFilters = Array.isArray(filters) ? filters : [];
				this.eventsPagination = this.isCompetitionView ? null : pagination;
			} catch (_error) {
				if (requestId !== this.eventsRequestId) {
					return;
				}
				if (_error?.code === 'ERR_CANCELED' || _error?.name === 'CanceledError') {
					return;
				}
				throw _error;
			} finally {
				if (requestId === this.eventsRequestId) {
					this.isLoadingEvents = false;
				}
			}
		},
		resetCompetitionContentData() {
			if (!this.isCompetitionView || !this.selectedCompetition) {
				this.competitionContentView = COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS;
			}
			this.leagueTable = null;
			this.topScorerTables = null;
			this.isTableNotFound = false;
			this.isTopScorerNotFound = false;
		},
		async loadLeagueTable() {
			if (!this.selectedCompetition) {
				this.leagueTable = null;
				return;
			}

			const requestId = ++this.tableRequestId;

			try {
				this.isLoadingTable = true;
				const response = await httpClient.getCompetitionTable(this.selectedCompetition.id);
				if (requestId !== this.tableRequestId) {
					return;
				}
				this.leagueTable = response.data?.entries ? response.data : null;
				this.isTableNotFound = false;
			} catch (_error) {
				if (requestId !== this.tableRequestId) {
					return;
				}
				if (_error?.code === 'ERR_CANCELED' || _error?.name === 'CanceledError') {
					return;
				}
				this.leagueTable = null;
				this.isTableNotFound = _error?.response?.status === 404;
				if (this.isTableNotFound) {
					resetCompetitionContentView(this, COMPETITION_CONTENT_VIEWS.TABLE);
				}
			} finally {
				if (requestId === this.tableRequestId) {
					this.isLoadingTable = false;
				}
			}
		},
		async loadTopScorerTables() {
			if (!this.selectedCompetition) {
				this.topScorerTables = null;
				return;
			}

			const requestId = ++this.topScorerRequestId;

			try {
				this.isLoadingTopScorerTables = true;
				const response = await httpClient.getCompetitionTopScorers(this.selectedCompetition.id);
				if (requestId !== this.topScorerRequestId) {
					return;
				}
				this.topScorerTables = response.data?.length > 0 && response.data[0].entries?.data?.length > 0 ? response.data : null;
				this.isTopScorerNotFound = false;
			} catch (_error) {
				if (requestId !== this.topScorerRequestId) {
					return;
				}
				if (_error?.code === 'ERR_CANCELED' || _error?.name === 'CanceledError') {
					return;
				}
				this.topScorerTables = null;
				this.isTopScorerNotFound = _error?.response?.status === 404;
				if (this.isTopScorerNotFound) {
					resetCompetitionContentView(this, COMPETITION_CONTENT_VIEWS.TOP_SCORER);
				}
			} finally {
				if (requestId === this.topScorerRequestId) {
					this.isLoadingTopScorerTables = false;
				}
			}
		},
		syncURL() {
			const url = new URL(window.location.href);
			const params = toURLParams(this.queryState);
			const supportedKeys = new Set(Object.keys(createDefaultQueryState()));

			for (const key of supportedKeys) {
				url.searchParams.delete(key);
			}

			for (const [key, value] of params.entries()) {
				url.searchParams.set(key, value);
			}

			window.history.replaceState(null, '', url.toString());
		},
		async updateQuery(patch) {
			const previousState = this.queryState;
			const nextCandidate = {
				...this.queryState,
				...patch,
			};

			if (shouldResetPagination(this.queryState, nextCandidate) && this.queryState.view !== EVENT_CENTER_VIEWS.COMPETITIONS) {
				nextCandidate.page = 1;
			}

			const { state } = sanitizeQueryState({
				rawQuery:      nextCandidate,
				contextConfig: this.contextConfig,
				competitions:  this.competitions,
				fallbackState: this.queryState,
			});

			this.queryState = this.resolveAllowedAllEventsView(state);
			this.syncURL();

			if (shouldFetchEventsFromServer(previousState, this.queryState)) {
				await this.loadEvents();
			}

			if (shouldResetCompetitionContentData(previousState, this.queryState)) {
				this.resetCompetitionContentData();
			}
		},
		handleScopeChange(scope) {
			if (scope === 'competitions') {
				const defaultCompetitionId = this.competitionOptions[0]?.value || null;
				this.updateQuery({
					view:        EVENT_CENTER_VIEWS.COMPETITIONS,
					competition: defaultCompetitionId,
				});
				return;
			}

			const enabledAllEventsViews = this.getEnabledAllEventsViews();
			if (enabledAllEventsViews.length === 0) {
				return;
			}

			this.updateQuery({
				view:        enabledAllEventsViews[0],
				competition: null,
				subview:     null,
				substage:    null,
			});
		},
		handleAllEventsViewChange(view) {
			this.updateQuery({
				view,
			});
		},
		handleCompetitionChange(competitionId) {
			this.competitionContentView = COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS;
			this.updateQuery({
				view:        EVENT_CENTER_VIEWS.COMPETITIONS,
				competition: competitionId,
				subview:     null,
				substage:    null,
			});
		},
		handleCompetitionSubviewChange(subview) {
			this.competitionContentView = COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS;
			this.updateQuery({
				view:     EVENT_CENTER_VIEWS.COMPETITIONS,
				subview,
				substage: null,
			});
		},
		handleCompetitionSubStageChange(subStage) {
			this.updateQuery({
				view:     EVENT_CENTER_VIEWS.COMPETITIONS,
				substage: subStage,
			});
		},
		handleCompetitionContentChange(contentView) {
			this.competitionContentView = contentView;
			this.ensureCompetitionContentData(contentView);
			if (contentView === COMPETITION_CONTENT_VIEWS.FIXTURES_RESULTS) {
				this.$nextTick(() => {
					this.$refs.eventCenterView?.resetMatchdaysView();
				});
			}
		},
		async ensureCompetitionContentData(contentView) {
			if (!this.isCompetitionView || !this.selectedCompetition) {
				return;
			}

			if (
				contentView === COMPETITION_CONTENT_VIEWS.TABLE &&
				this.hasSelectedCompetitionLeagueTable &&
				!this.isLoadingTable &&
				!this.leagueTable &&
				!this.isTableNotFound
			) {
				await this.loadLeagueTable();
				return;
			}

			if (
				contentView === COMPETITION_CONTENT_VIEWS.TOP_SCORER &&
				this.hasSelectedCompetitionTopScorerTables &&
				!this.isLoadingTopScorerTables &&
				!this.topScorerTables &&
				!this.isTopScorerNotFound
			) {
				await this.loadTopScorerTables();
			}
		},
		handlePaginationChange(page) {
			this.updateQuery({
				page,
			});
		},
		handleSearchChange(valueOrEvent) {
			const searchValue = typeof valueOrEvent === 'string'
				? valueOrEvent
				: valueOrEvent?.target?.value || '';

			this.updateQuery({
				q: searchValue,
			});
		},
		handleFilterChange({ key, value }) {
			this.updateQuery({
				[key]: value,
			});
		},
	},
};
</script>
